import React, { useState } from "react";
import "./notionTemplates.css";

import {
  ArrowRight,
  ShieldCheck,
  Database,
  Globe,
  BarChart3,
  PlayCircle,
  X,
} from "lucide-react";

const HERO_VIDEO =
  "https://www.youtube.com/embed/vIl8uzXCnsA";

const products = [
  {
    title: "The ESMS & Stakeholder Hub",
    icon: <ShieldCheck size={28} />,
    description:
      "Eliminate fragmented spreadsheets. This module centralizes grievance management, stakeholder engagement, and E&S performance tracking. Built to satisfy IFC Performance Standard 1 and EBRD PR 1 & 10.",
    feature: "Automated Grievance Redress Mechanism (GRM) dashboard.",
    bestFor: "Project managers and E&S leads in the field.",
    video: "https://www.youtube.com/embed/yoFmelzzfGM",
  },
  {
    title: "The RLR Master System",
    icon: <Database size={28} />,
    description:
      "Manage complex resettlement and land acquisition with precision. This system handles census data, asset valuation, and compensation tracking in a single relational database, ensuring alignment with IFC PS5.",
    feature: 'Live "Compensation Status" tracking per household.',
    bestFor: "Resettlement officers and Land Acquisition consultants.",
    video: "https://www.youtube.com/embed/x7yNPAb4b08",
  },
  {
    title: "The DMA & Climate Risk Hub",
    icon: <Globe size={28} />,
    description:
      "Built for the new era of sustainability reporting. Quantify Double Materiality (Impact & Financial) and track TCFD-aligned climate hazards. Designed for CSRD/ESRS compliance.",
    feature: "Quantitative scoring engine for material ESG topics.",
    bestFor: "ESG Directors and Corporate Sustainability Leads.",
    video: "https://www.youtube.com/embed/aeJdRUmfL7w",
  },
  {
    title: "The Full Suite (Integrated Ecosystem)",
    icon: <BarChart3 size={28} />,
    description:
      "Our flagship solution. It connects the ESMS, RLR, and DMA modules into a single Executive Command Centre. See how a climate hazard affects a stakeholder group or how a grievance impacts a resettlement timeline in real-time.",
    feature:
      "High-level executive oversight of the entire project lifecycle.",
    bestFor: "Enterprise-level project governance and Lenders.",
    featured: true,
    video: "https://www.youtube.com/embed/cKDxfRdT7gw",
  },
];

const values = [
  {
    title: "Data Sovereignty",
    text: "Unlike SaaS, you own your data. It stays in your workspace.",
  },
  {
    title: "Audit-Ready",
    text: "Every entry has a timestamp and relational link, creating a defensible audit trail.",
  },
  {
    title: "No Subscriptions",
    text: "One-time purchase for a lifetime of project governance.",
  },
  {
    title: "Cloud-Native",
    text: "Access your ESMS or RLR data from the field on any device.",
  },
];

const NotionTemplates = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);

  const openVideo = (videoUrl) => {
    setSelectedVideo(videoUrl);
    document.body.style.overflow = "hidden";
  };

  const closeVideo = () => {
    setSelectedVideo(null);
    document.body.style.overflow = "auto";
  };

  return (
    <div className="notion-page">
      {/* HERO */}
      <section className="notion-hero">
        <div className="hero-overlay"></div>

        <div className="hero-content">
          <span className="hero-badge">
            ESG Digital Infrastructure
          </span>

          <h1>
            The ESG Core <span>Digital Suite</span>
          </h1>

          <p>
            Professional-grade Notion systems for international
            project finance and corporate sustainability.
          </p>

          <div className="hero-buttons">
            <a
              href="https://essolutions.gumroad.com/"
              target="_blank"
              rel="noreferrer"
              className="primary-btn"
            >
              Explore Digital ESG Suite
              <ArrowRight size={18} />
            </a>

            <button
              type="button"
              className="secondary-btn"
              onClick={() => openVideo(HERO_VIDEO)}
            >
              <PlayCircle size={18} />
              Watch Demo
            </button>
          </div>
        </div>

        <div className="floating-orb orb1"></div>
        <div className="floating-orb orb2"></div>
        <div className="floating-orb orb3"></div>
      </section>

      {/* PRODUCTS */}
      <section className="products-section">
        <div className="section-header">
          <span>Solutions</span>
          <h2>Purpose-Built ESG Systems</h2>
        </div>

        <div className="products-grid">
          {products.map((product, index) => (
            <div
              className={`product-card ${
                product.featured ? "featured-card" : ""
              }`}
              key={index}
            >
              <div className="product-icon">
                {product.icon}
              </div>

              <h3>{product.title}</h3>

              <p className="product-description">
                {product.description}
              </p>

              <div className="product-feature">
                <strong>Key Feature:</strong>
                <span>{product.feature}</span>
              </div>

              <div className="product-bestfor">
                <strong>Best For:</strong>
                <span>{product.bestFor}</span>
              </div>

              <div className="product-actions">
                <button
                  type="button"
                  className="watch-btn"
                  onClick={() => openVideo(product.video)}
                >
                  <PlayCircle size={16} />
                  Watch Walkthrough
                </button>

                <a
                  href="https://essolutions.gumroad.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="shop-btn"
                >
                  View Digital ESG Suite
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* VALUE SECTION */}
      <section className="value-section">
        <div className="section-header">
          <span>Why Notion?</span>
          <h2>Built for Modern ESG Governance</h2>
        </div>

        <div className="value-grid">
          {values.map((item, index) => (
            <div className="value-card" key={index}>
              <div className="value-number">
                0{index + 1}
              </div>

              <h3>{item.title}</h3>

              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="cta-card">
          <h2>Need a Custom Setup?</h2>

          <p>
            If your project requires bespoke configuration or a
            lender-mandated workflow, we provide technical
            integration and ESG system implementation services.
          </p>

          <div className="cta-buttons">
            <a
              href="mailto:info@essolutions.ai"
              className="primary-btn"
            >
              Contact Us
            </a>

            <a
              href="https://calendly.com/"
              target="_blank"
              rel="noreferrer"
              className="secondary-btn"
            >
              Book a Consultation
            </a>
          </div>
        </div>
      </section>

      {/* VIDEO MODAL */}
      {selectedVideo && (
        <div
          className="video-modal-overlay"
          onClick={closeVideo}
        >
          <div
            className="video-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="close-video-btn"
              onClick={closeVideo}
            >
              <X size={22} />
            </button>

            <div className="video-wrapper">
              <iframe
                src={`${selectedVideo}?autoplay=1`}
                title="Walkthrough Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotionTemplates;